import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateHealthcareJourneyNumber,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type z from "zod";

export const EditHealthcareJourneyNumber = ({
  currentHealthcareJourneyNumber,
  onSuccess,
  patientId,
  fieldConfig,
}: {
  currentHealthcareJourneyNumber: string | null | undefined;
  onSuccess: () => void;
  patientId: string;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      healthcareJourneyNumber: currentHealthcareJourneyNumber,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (healthcareJourneyNumber: string | null | undefined) =>
      updateHealthcareJourneyNumber(patientId, healthcareJourneyNumber),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const { t } = useLingui();

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.healthcareJourneyNumber);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar sjukresenummer`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <InputField
              label={t`Sjukresenummer`}
              showOptionalLabel={
                fieldConfig === informationFieldConfigValueSchema.enum.optional
              }
              errorMessage={errors.healthcareJourneyNumber?.message}
              {...register("healthcareJourneyNumber", {
                pattern: {
                  value: /^\d{8}$/,
                  message: t`Ange sjukresenummer med 8 siffror`,
                },
                required: {
                  value:
                    fieldConfig ===
                    informationFieldConfigValueSchema.enum.required,
                  message: t`Ange sjukresenummer`,
                },
              })}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
